
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/lib/toast";

interface CreateGoalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGoalCreated: () => void;
}

interface Client {
  id: string;
  name: string;
}

export function CreateGoalDialog({ isOpen, onClose, onGoalCreated }: CreateGoalDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    client_name: "",
    goal_type: "postagens" as "postagens" | "faturamento" | "outros",
    target_value: "",
    due_date: ""
  });

  // Carregar clientes
  useEffect(() => {
    const loadClients = async () => {
      if (!user || !isOpen) return;

      try {
        let targetCompanyId: string | null = null;

        // Verificar se é colaborador
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          targetCompanyId = collaboratorData.company_id;
        } else {
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', user.id)
            .single();
          targetCompanyId = userData?.company_id || null;
        }

        if (!targetCompanyId) return;

        const { data, error } = await supabase
          .from('clients')
          .select('id, name')
          .eq('company_id', targetCompanyId)
          .eq('status', 'active')
          .order('name');

        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };

    loadClients();
  }, [user, isOpen]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      client_name: "",
      goal_type: "postagens",
      target_value: "",
      due_date: ""
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.client_name || !formData.due_date) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (!user) {
      toast.error("Usuário não autenticado");
      return;
    }

    setLoading(true);
    try {
      // Buscar company_id do usuário
      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        toast.error("Empresa não encontrada");
        return;
      }

      // Buscar todos os colaboradores ativos da empresa para adicionar na visibilidade
      const { data: collaborators } = await supabase
        .from('collaborators')
        .select('collaborator_id')
        .eq('company_id', targetCompanyId)
        .eq('status', 'active');

      // Buscar proprietários da empresa
      const { data: companyData } = await supabase
        .from('companies')
        .select('owner_ids')
        .eq('id', targetCompanyId)
        .single();

      // Criar lista de usuários que podem ver a meta
      const visibleTo = [user.id]; // Sempre incluir o criador
      
      // Adicionar colaboradores ativos com permissão de reports
      if (collaborators) {
        for (const collab of collaborators) {
          // Verificar se o colaborador tem permissão de reports
          const { data: collabData } = await supabase
            .from('collaborators')
            .select('permissions')
            .eq('collaborator_id', collab.collaborator_id)
            .eq('company_id', targetCompanyId)
            .eq('status', 'active')
            .single();

          if (collabData && (collabData.permissions as any)?.reports && !visibleTo.includes(collab.collaborator_id)) {
            visibleTo.push(collab.collaborator_id);
          }
        }
      }
      
      // Adicionar proprietários da empresa
      if (companyData?.owner_ids && Array.isArray(companyData.owner_ids)) {
        companyData.owner_ids.forEach((ownerId: string) => {
          if (!visibleTo.includes(ownerId)) {
            visibleTo.push(ownerId);
          }
        });
      }

      const goalData = {
        title: formData.title,
        description: formData.description || null,
        client_name: formData.client_name,
        goal_type: formData.goal_type,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        current_value: 0,
        due_date: formData.due_date,
        completed: false,
        user_id: user.id,
        company_id: targetCompanyId,
        visible_to: visibleTo
      };

      const { error } = await supabase
        .from('goals')
        .insert([goalData]);

      if (error) throw error;

      toast.success("Meta criada com sucesso!");
      resetForm();
      onClose();
      onGoalCreated();
    } catch (error: any) {
      console.error('Error creating goal:', error);
      toast.error(`Erro ao criar meta: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Criar Nova Meta</DialogTitle>
          <DialogDescription>
            Defina uma meta para acompanhar o progresso do seu trabalho.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              Título *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="client_name" className="text-right">
              Cliente *
            </Label>
            <Select 
              value={formData.client_name} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, client_name: value }))}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Selecionar cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.name}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="goal_type" className="text-right">
              Tipo *
            </Label>
            <Select 
              value={formData.goal_type} 
              onValueChange={(value: "postagens" | "faturamento" | "outros") => 
                setFormData(prev => ({ ...prev, goal_type: value }))
              }
            >
              <SelectTrigger className="col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="postagens">Postagens</SelectItem>
                <SelectItem value="faturamento">Faturamento</SelectItem>
                <SelectItem value="outros">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="target_value" className="text-right">
              Meta
            </Label>
            <Input
              id="target_value"
              type="number"
              value={formData.target_value}
              onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value }))}
              placeholder="Ex: 10"
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="due_date" className="text-right">
              Prazo *
            </Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="description" className="text-right">
              Descrição
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="col-span-3"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Criando..." : "Criar Meta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
